import {
  ContentCopy as CopyIcon,
  VpnKey as KeyIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import {
  calculateLicenseStatus,
  formatExpiryDate,
  getDaysUntilExpiry,
} from "../../utils/licenseStatusUtils";
import { extractLicenseInfo } from "../../utils/licenseUtils";
import LicenseStatusChip from "../license/LicenseStatusChip";

const ProductLicenseDialog = ({ open, onClose, product }) => {
  const [hardwareId, setHardwareId] = useState("");
  const [savingHardwareId, setSavingHardwareId] = useState(false);
  const [licenseKey, setLicenseKey] = useState("");
  const [license, setLicense] = useState(null);
  const [currentStep, setCurrentStep] = useState("checking"); // 'input', 'checking', 'display'
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (open && product) {
      loadLicense();
    }
  }, [open, product]);

  const loadLicense = async () => {
    if (!product) return;

    setCurrentStep("checking");
    setSavingHardwareId(true);
    setError(null);

    try {
      // Check if there's an existing license
      const response = await userProductLicenseService.getMyProductLicense(
        product.id,
      );

      if (response && response.license) {
        setLicense(response.license);
        setLicenseKey(response.license.license_key);
        setCurrentStep("display");
      } else {
        setLicense(null);
        setError(
          "No license found. Please contact your administrator to request a license.",
        );
        setCurrentStep("input");
      }
    } catch (err) {
      console.error("Error checking for license:", err);
      setLicense(null);
      setError(
        "No license found. Please contact your administrator to request a license.",
      );
      setCurrentStep("input");
    } finally {
      setSavingHardwareId(false);
    }
  };

  const handleHardwareIdChange = (e) => {
    setHardwareId(e.target.value);
  };

  const handleSubmitHardwareId = async () => {
    if (!hardwareId.trim()) {
      setError("Please enter a valid hardware ID");
      return;
    }

    setSavingHardwareId(true);
    setCurrentStep("checking");
    setError(null);

    try {
      // First check if there's an existing license
      const response = await userProductLicenseService.getMyProductLicense(
        product.id,
      );

      if (response && response.license) {
        setLicense(response.license);
        setLicenseKey(response.license.license_key);
        setCurrentStep("display");
      } else {
        setLicense(null);
        setError(
          "No license found. Please contact your administrator to request a license.",
        );
        setCurrentStep("input");
      }
    } catch (err) {
      console.error("Error checking for license:", err);
      setLicense(null);
      setError(
        "No license found. Please contact your administrator to request a license.",
      );
      setCurrentStep("input");
    } finally {
      setSavingHardwareId(false);
    }
  };

  const handleCopyLicenseKey = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey);
      setSnackbar({
        open: true,
        message: "License key copied to clipboard!",
        severity: "success",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClose = () => {
    // Reset the state when closing
    setHardwareId("");
    setLicenseKey("");
    setLicense(null);
    setError(null);
    setCurrentStep("checking");
    onClose();
  };

  const licenseInfo =
    license && license.license_key
      ? extractLicenseInfo(license.license_key)
      : null;
  const licenseStatus = calculateLicenseStatus(license);
  const daysUntilExpiry = getDaysUntilExpiry(license);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <KeyIcon color="secondary" />
          <Typography variant="h6">License for {product?.name}</Typography>
        </Box>
        {license && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LicenseStatusChip license={license} size="medium" />
            {license.expiry_date && (
              <Chip
                icon={<ScheduleIcon />}
                label={formatExpiryDate(license)}
                size="small"
                variant="outlined"
                color={
                  daysUntilExpiry && daysUntilExpiry <= 30
                    ? "warning"
                    : "default"
                }
              />
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {currentStep === "input" && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Enter your hardware ID to check for license. Contact support if
              you need assistance.
            </Typography>

            <TextField
              fullWidth
              label="Hardware ID"
              value={hardwareId}
              onChange={handleHardwareIdChange}
              placeholder="Enter your hardware ID here"
              helperText="Example: aInVru3uU54X1u13vlLcyyJQ858="
              required
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {currentStep === "checking" && (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography>Checking for license...</Typography>
          </Box>
        )}

        {currentStep === "display" && license && (
          <Box>
            {/* Compact License Information */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box>
                  <LicenseStatusChip
                    license={license}
                    size="small"
                    showTooltip={false}
                  />
                </Box>
              </Grid>

              {license.expiry_date && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Expires
                  </Typography>
                  <Typography variant="body2">
                    {formatExpiryDate(license)}
                    {daysUntilExpiry !== null && daysUntilExpiry >= 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        (
                        {daysUntilExpiry === 0
                          ? "expires today"
                          : daysUntilExpiry === 1
                            ? "1 day"
                            : `${daysUntilExpiry} days`}
                        )
                      </Typography>
                    )}
                  </Typography>
                </Grid>
              )}

              {license.created_at && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(license.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}

              {license.hardware_ids && license.hardware_ids.length > 0 && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Devices
                  </Typography>
                  <Typography variant="body2">
                    {license.hardware_ids.length} registered
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* License Key Section */}
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              License Key
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                fontFamily: "monospace",
                position: "relative",
                fontSize: "0.75rem",
                wordBreak: "break-all",
                lineHeight: 1.4,
              }}
            >
              <Typography
                variant="body2"
                component="code"
                sx={{
                  pr: 5,
                  fontSize: "0.75rem",
                  color: "primary.dark",
                }}
              >
                {licenseKey}
              </Typography>
              <Tooltip title="Copy License Key" placement="top">
                <IconButton
                  onClick={handleCopyLicenseKey}
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>

            <Alert
              severity={
                licenseStatus === "active"
                  ? "success"
                  : licenseStatus === "expired"
                    ? "error"
                    : "warning"
              }
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                {licenseStatus === "active" &&
                  `License is active and ready to use.`}
                {licenseStatus === "expired" &&
                  `License has expired. Contact administrator to renew.`}
                {licenseStatus === "expiring_soon" &&
                  `License expires soon. Contact administrator to renew.`}
                {licenseStatus === "inactive" &&
                  `License is inactive. Contact administrator.`}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        {currentStep === "input" && (
          <>
            <Button onClick={handleClose} variant="outlined" size="small">
              Cancel
            </Button>
            <Button
              onClick={handleSubmitHardwareId}
              variant="contained"
              color="primary"
              disabled={!hardwareId || savingHardwareId}
              startIcon={
                savingHardwareId ? <CircularProgress size={14} /> : <KeyIcon />
              }
              size="small"
            >
              {savingHardwareId ? "Checking..." : "Check License"}
            </Button>
          </>
        )}

        {currentStep === "checking" && (
          <Button onClick={handleClose} variant="outlined" size="small">
            Cancel
          </Button>
        )}

        {currentStep === "display" && (
          <>
            <Button
              onClick={handleCopyLicenseKey}
              variant="contained"
              color="primary"
              startIcon={<CopyIcon />}
              size="small"
            >
              Copy Key
            </Button>
            <Button onClick={handleClose} variant="outlined" size="small">
              Close
            </Button>
          </>
        )}
      </DialogActions>

      {/* Snackbar for copy notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ProductLicenseDialog;
